import type { HttpError } from '@refinedev/core';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { IUser } from 'interfaces/users';
import { InputMask } from 'components/form-items/InputMask';
import { PageHeader } from 'components/professor_area/PageHeader';
import { InfoCircleOutlined, SaveOutlined, TeamOutlined } from '@ant-design/icons';
import { UserEditAddressSection } from './UserEditAddressSection';
import { UserEditPasswordModal } from './UserEditPasswordModal';
import { isProfilePristine } from './profileFormUtils';
import { useEffect, useState } from 'react';
import { useForm } from '@refinedev/antd';
import { fullNameValidatorRule } from 'utils/validators';

export const UserEdit = () => {
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  /** Increments only after a successful profile save; address UI clears local override. */
  const [profileSaveNonce, setProfileSaveNonce] = useState(0);
  /** Bumps when the form values change or server `record` updates (after resetFields). */
  const [profileCompareTick, setProfileCompareTick] = useState(0);

  const {
    form,
    formProps,
    saveButtonProps,
    queryResult,
    formLoading,
    onFinish
  } = useForm<IUser>({
    redirect: 'edit',
    onMutationSuccess: () => {
      setProfileSaveNonce(n => n + 1);
    },
    errorNotification: (error: HttpError | undefined) => ({
      type: 'error',
      message: error?.message ?? 'Tente novamente mais tarde',
      description: 'Não foi possível salvar o aluno.'
    })
  });

  const { onValuesChange: profileFormOnValuesChange, ...profileFormProps } =
    formProps;

  const record = queryResult?.data?.data;

  useEffect(() => {
    setProfileCompareTick(t => t + 1);
  }, [
    record?.id,
    record?.email,
    record?.name,
    record?.personal_info?.full_name,
    record?.personal_info?.document_info,
    record?.personal_info?.phone_number
  ]);

  void profileCompareTick;

  const profileDirty = Boolean(
    record &&
      !isProfilePristine(
        form.getFieldsValue(true) as Record<string, unknown>,
        record
      )
  );

  const onSubmitProfile = async (
    values: Record<string, unknown> & {
      personal_info_attributes?: {
        document_info?: string;
        phone_number?: string;
      };
    }
  ) => {
    const payload: Record<string, unknown> = {
      ...values,
      personal_info_attributes: {
        ...values.personal_info_attributes,
        document_type: 'cpf',
        document_info: values.personal_info_attributes?.document_info?.replace(
          /\D/g,
          ''
        ),
        phone_number: values.personal_info_attributes?.phone_number?.replace(
          /\D/g,
          ''
        )
      }
    };

    try {
      await onFinish(payload as never);
    } catch {
      // tratado pelo errorNotification
    }
  };

  const profileSaveButtonProps = {
    ...saveButtonProps,
    disabled: Boolean(saveButtonProps.disabled || formLoading || !profileDirty)
  };

  return (
    <EditLayout
      title="Editar aluno"
      isLoading={formLoading}
      footerButtonProps={{ hidden: true }}
    >
      <PageHeader
        title={record?.name}
        icon={TeamOutlined}
        meta={<Tag>{record?.email}</Tag>}
      />

      <Card>
        {record && (
          <>
            <Form
              layout="vertical"
              {...profileFormProps}
              onFinish={onSubmitProfile}
              onValuesChange={(changedValues, allValues) => {
                profileFormOnValuesChange?.(changedValues, allValues);
                setProfileCompareTick(t => t + 1);
              }}
            >
              <Typography.Title level={5}>Conta</Typography.Title>
              <Row gutter={[16, 0]} align="top">
                <Col xs={24} md={12}>
                  <Form.Item
                    label="E-mail"
                    name="email"
                    initialValue={record.email}
                    rules={[
                      { required: true, message: 'Campo obrigatório' },
                      { type: 'email', message: 'Insira um e-mail válido' }
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Senha">
                    <Button
                      type="default"
                      onClick={() => setPasswordModalOpen(true)}
                    >
                      Alterar senha
                    </Button>
                  </Form.Item>
                </Col>
              </Row>

              <Divider />

              <Typography.Title level={5}>
                Informações pessoais
              </Typography.Title>
              <Row gutter={[16, 0]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        Nome Completo
                        <Tooltip title="Utilizado para emissão de certificados. Insira o nome conforme documento oficial.">
                          <InfoCircleOutlined className="ml-1 text-xs text-blue-500 align-middle" />
                        </Tooltip>
                      </span>
                    }
                    name={['personal_info_attributes', 'full_name']}
                    initialValue={record.personal_info?.full_name}
                    rules={[
                      { required: true, message: 'Campo obrigatório' },
                      fullNameValidatorRule()
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Como deseja ser chamado(a)"
                    name="name"
                    initialValue={record.name}
                  >
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="CPF"
                    name={['personal_info_attributes', 'document_info']}
                    initialValue={record.personal_info?.document_info}
                    rules={[
                      {
                        pattern: /^(?:\d{3}\.){2}\d{3}-\d{2}$/,
                        message: 'Insira um CPF válido'
                      },
                      { required: true, message: 'Campo obrigatório' }
                    ]}
                  >
                    <InputMask mask="000.000.000-00" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Telefone"
                    name={['personal_info_attributes', 'phone_number']}
                    initialValue={record.personal_info?.phone_number}
                    rules={[
                      {
                        pattern: /^\(\d{2}\) \d{5}-\d{4}$/,
                        message: 'Insira um numero de telefone válido'
                      }
                    ]}
                  >
                    <InputMask mask="(00) 00000-0000" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Row justify="end" style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                {...profileSaveButtonProps}
              >
                Salvar
              </Button>
            </Row>

            <Divider />

            <UserEditAddressSection
              record={record}
              profileSaveNonce={profileSaveNonce}
            />
          </>
        )}
      </Card>

      <UserEditPasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        userId={record?.id}
      />
    </EditLayout>
  );
};
